/**
 * The layout and motion decisions the whole console shares.
 *
 * Fluent gives colour, type and spacing tokens. What it has no opinion about is the shape of a
 * page — how wide content runs, how a card lifts, how fast anything moves — and those are exactly
 * the decisions that make a console look designed rather than assembled. Kept here so they are
 * one decision each rather than eighteen.
 */

/**
 * Where the layout has to change shape.
 *
 * One breakpoint, not a scale. The shop floor works on a phone and the office works on a laptop,
 * and every screen is written to survive the gap by reflowing rather than by having a second
 * layout — a second breakpoint is a second layout to keep true.
 *
 * In rem, so it moves with the reader's text size: someone who has turned their font up is closer
 * to a phone layout whatever their screen measures.
 */
export const PHONE = "@media (max-width: 40rem)";

/** Where the sidebar has room to stand beside the content rather than under it. */
export const DESKTOP = "@media (min-width: 60rem)";

/** Nothing narrower than this is comfortable to tap; smaller controls get missed and hit twice. */
export const TAP_TARGET = "2.75rem";

/**
 * How things move.
 *
 * Short and eased-out: an operations tool is used for eight hours, and animation that reads as
 * characterful on the first go reads as a delay on the fiftieth. Anything past about 200ms starts
 * to feel like waiting rather than responding.
 *
 * Every use of these belongs inside a `prefers-reduced-motion` guard, which `index.css` applies
 * globally rather than per component.
 */
export const motion = {
  fast: "120ms cubic-bezier(0.33, 0, 0.2, 1)",
  base: "180ms cubic-bezier(0.33, 0, 0.2, 1)",
  /** For something entering the page, which may take slightly longer than a hover. */
  enter: "240ms cubic-bezier(0.16, 1, 0.3, 1)",
} as const;

/**
 * Elevation, as a pair: the resting shadow and the one it lifts to.
 *
 * Written as literal shadows rather than Fluent's `shadow4`/`shadow8` tokens because the pair has
 * to be tuned together — a hover state that changes blur without changing offset reads as a
 * glitch rather than a lift.
 */
export const elevation = {
  restLight: "0 1px 2px rgba(9, 23, 37, 0.06), 0 0 0 1px rgba(9, 23, 37, 0.05)",
  hoverLight: "0 6px 16px rgba(9, 23, 37, 0.10), 0 0 0 1px rgba(9, 23, 37, 0.07)",
} as const;
